/** @module qt.page
 *  @description webview 相关设置
 */
import { createAppCallback, invokeIosHandler } from '../core';
import { invokeSchema } from '../base';
import { inIosApp } from '../env';

/** 设置导航条
 * @example 
 * ```javascript
 * qt.page.setNavbar({ show: false })
  .then(res => {
    console.log(res);
  })
  .catch(e => {
    console.log(e.message);
  });
 * ```
 */
export function setNavbar(
  option: {
    /** 显示导航条 */
    show?: boolean;
    /** 隐藏关闭按钮，android无效 */
    hideClose?: boolean;
    /** 隐藏分享 */
    hideShare?: boolean;
  } = {},
): Promise<unknown> {
  const { show = true, hideClose = false, hideShare = false } = option;
  if ('hideClose' in option || 'hideShare' in option) {
    return invokeSchema('qingtingfm://action.qingting.fm/showNavigationBar', { show }).then(() => {
      if (inIosApp) {
        return invokeIosHandler('setWebviewConfig', {
          hide_close: hideClose,
          hide_right_button: hideShare,
        });
      }
      return window.QTJsPage.setShowState(
        JSON.stringify({
          hide_close: hideClose,
          right_button: hideShare ? 'none' : 'share',
        }),
        null,
        null,
      );
    });
  }
  return invokeSchema('qingtingfm://action.qingting.fm/showNavigationBar', { show });
}

/**
 * 设置状态条
 * @example
 * ```javascript
 * qt.page.setStatusBar({ style: 'light' })
  .then(res => {
    console.log(res);
  })
  .catch(e => {
    console.log(e.message);
  });

 * ```
 */
export function setStatusBar({
  style = 'default',
}: {
  /** 状态栏样式
   * @defaultValue 'default'
   */
  style?: 'default' | 'light' | string;
}): Promise<void | unknown> {
  return invokeSchema('qingtingfm://action.qingting.fm/setStatusBarStyle', {
    status_bar_style: style,
  });
}

/**
 * 设置分享按钮内容
 * @version: IOS: 8.4.2+; Android: 8.4.2+
 * @example
 * ```javascript
 * qt.page.setShare({
  link: 'https://my-link.com',
  imageUrl: 'https://my-image.jpg',
  title: 'my title',
  desc: 'my desc'
})
  .then(res => {
    console.log(res);
  })
  .catch(e => {
    console.log(e.message);
  });
 * ```
 */
export function setShare({
  link,
  imageUrl,
  title,
  desc,
}: {
  link?: string;
  imageUrl?: string;
  title: string;
  desc?: string;
}): Promise<unknown> {
  return invokeSchema('qingtingfm://action.qingting.fm/setshare', {
    h5_link: link,
    h5_title: title,
    h5_desc: desc,
    h5_image: imageUrl,
    hide_right_button: 0,
  });
}

/** 设置分享回调
 * @version:  8.6.0+
 * @example
 * ```javascript
 * qt.page.setShareCallback({
  onSuccess: (res) => {
    console.log(res);
  }
});

 * ```
 */
export function setShareCallback({
  onSuccess,
}: {
  /** 分享成功后的回调函数 */
  onSuccess: (res: unknown) => void;
}): Promise<unknown> {
  const cb = createAppCallback(onSuccess, 'shareCallback', false);
  return invokeSchema('qingtingfm://action.qingting.fm/setShareCallback', {
    shareCallback: cb.cbName,
  });
}

/** @ignore */
export const page = {
  setNavbar,
  setStatusBar,
  setShare,
  setShareCallback,
};
